import type { BadgeTone } from '@/components/ui/badge';
import type {
  CustomDomain,
  CustomDomainStatus,
  CustomDomainStatusReason,
} from '@/api/customDomains/customDomainsApi.types';

export type CustomDomainStepKey = 'dnsRecords' | 'ownership' | 'certificate' | 'live';
export type CustomDomainStepState = 'done' | 'inProgress' | 'upcoming' | 'failed';

export interface CustomDomainStep {
  key: CustomDomainStepKey;
  state: CustomDomainStepState;
  reason: CustomDomainStatusReason | null;
}

const WAITING_FOR_RECORDS_REASONS: ReadonlySet<CustomDomainStatusReason> = new Set([
  'ownership_record_missing',
  'hostname_not_found',
  'dns_lookup_failed',
]);

function step(
  key: CustomDomainStepKey,
  state: CustomDomainStepState,
  reason: CustomDomainStatusReason | null = null
): CustomDomainStep {
  return { key, state, reason };
}

export function isWaitingForRecordsReason(reason: CustomDomainStatusReason | null): boolean {
  return reason === null || WAITING_FOR_RECORDS_REASONS.has(reason);
}

function resolvePendingDnsSteps(customDomain: CustomDomain): CustomDomainStep[] {
  const dnsStep = isWaitingForRecordsReason(customDomain.statusReason)
    ? step('dnsRecords', 'inProgress')
    : step('dnsRecords', 'failed', customDomain.statusReason);
  return [
    dnsStep,
    step('ownership', 'upcoming'),
    step('certificate', 'upcoming'),
    step('live', 'upcoming'),
  ];
}

export function resolveCustomDomainSteps(customDomain: CustomDomain): CustomDomainStep[] | null {
  switch (customDomain.status) {
    case 'pending_dns':
      return resolvePendingDnsSteps(customDomain);
    case 'verified':
      return [
        step('dnsRecords', 'done'),
        step('ownership', 'done'),
        step('certificate', 'inProgress'),
        step('live', 'upcoming'),
      ];
    case 'certificate_failed':
      return [
        step('dnsRecords', 'done'),
        step('ownership', 'done'),
        step('certificate', 'failed', customDomain.statusReason),
        step('live', 'upcoming'),
      ];
    case 'active':
      return [
        step('dnsRecords', 'done'),
        step('ownership', 'done'),
        step('certificate', 'done'),
        step('live', 'done'),
      ];
    case 'lost':
      return [
        step('dnsRecords', 'failed', customDomain.statusReason),
        step('ownership', 'upcoming'),
        step('certificate', 'upcoming'),
        step('live', 'upcoming'),
      ];
    default:
      return null;
  }
}

export function shouldCheckAutomatically(customDomain: CustomDomain): boolean {
  return customDomain.status === 'pending_dns' || customDomain.status === 'lost';
}

const STATUS_BADGE_TONE: Record<CustomDomainStatus, BadgeTone> = {
  pending_dns: 'warning',
  verified: 'neutral',
  active: 'success',
  certificate_failed: 'danger',
  lost: 'danger',
  disabled: 'muted',
  removing: 'muted',
};

export function resolveCustomDomainStatusTone(status: CustomDomainStatus): BadgeTone {
  return STATUS_BADGE_TONE[status];
}

export function shouldShowDnsRecords(customDomain: CustomDomain): boolean {
  return (
    customDomain.status === 'pending_dns' ||
    customDomain.status === 'certificate_failed' ||
    customDomain.status === 'lost'
  );
}

export function canCheckCustomDomain(customDomain: CustomDomain): boolean {
  return shouldCheckAutomatically(customDomain);
}

export function canMakeCustomDomainPrimary(
  customDomain: CustomDomain,
  customDomainCount: number
): boolean {
  return (
    customDomain.status === 'active' && customDomain.isPrimary === false && customDomainCount > 1
  );
}

export function hasWwwCompanionDomain(
  customDomain: CustomDomain,
  allCustomDomains: CustomDomain[]
): boolean {
  const companionHostname = 'www.' + customDomain.hostname;
  return allCustomDomains.some(function matchesCompanion(candidate) {
    return candidate.hostname === companionHostname;
  });
}
